// src/App.jsx

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Layout } from './components/layout/Layout';
import { LoadingScreen } from './components/shared/UI';

// Pages — Auth
import { LoginPage, RegisterPage } from './pages/Auth';
import { DestinationSelector, getLoginDefault, clearLoginDefault } from './pages/DestinationSelector';

// Pages — Employer
import { EmployerDashboard }  from './pages/employer/EmployerDashboard';
import { EmployerJobAds }     from './pages/employer/EmployerJobAds';
import { JobAdMockup }        from './pages/employer/JobAdMockup';
import { EmployerWorkplaces } from './pages/employer/EmployerWorkplaces';
import { EmployerJobLibrary } from './pages/employer/EmployerJobLibrary';
import { EmployerMembers }    from './pages/employer/EmployerMembers';
import { OrgSettings }        from './pages/employer/OrgSettings';
import { EmployerOverview }   from './pages/employer/profile/EmployerOverview';
import { EmployerTeam }       from './pages/employer/profile/EmployerTeam';
import { EmployerContacts }   from './pages/employer/profile/EmployerContacts';
import { EmployerMedia }      from './pages/employer/profile/EmployerMedia';
import { EmployerCandidates } from './pages/employer/EmployerCandidates';
import { EmployerMatchDetail } from './pages/employer/EmployerMatchDetail';

// Pages — Seeker
import { SeekerDashboard }      from './pages/seeker/SeekerDashboard';
import { SeekerProfile }        from './pages/seeker/SeekerProfile';
import { SeekerCVEditor }       from './pages/seeker/SeekerCVEditor';
import { SeekerJobs }           from './pages/seeker/SeekerJobs';
import { SeekerPersonal }       from './pages/seeker/SeekerPersonal';
import { SeekerWorkPreferences } from './pages/seeker/SeekerWorkPreferences';
import { SeekerAvailability }   from './pages/seeker/SeekerAvailability';
import { SeekerSkills }         from './pages/seeker/SeekerSkills';
import { SeekerLanguages }      from './pages/seeker/SeekerLanguages';
import { SeekerQualifications } from './pages/seeker/SeekerQualifications';
import { SeekerExperience }     from './pages/seeker/SeekerExperience';
import { SeekerReferences }     from './pages/seeker/SeekerReferences';
import { SeekerCVImport }       from './pages/seeker/SeekerCVImport';
import { SeekerSummary }        from './pages/seeker/SeekerSummary';
import { SeekerJobDetail }      from './pages/seeker/SeekerJobDetail';

// Pages — Admin
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { AdminConfig }    from './pages/admin/AdminConfig';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

// ── Route Guards ──────────────────────────────────────

const ProtectedRoute = ({ children, role }) => {
  const { user, loading, orgs, orgsLoading } = useAuth();
  if (loading || orgsLoading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (role === 'EMPLOYER') {
    // Employer routes require org membership — redirect to destination selector if none
    if (!orgs.length) return <Navigate to="/select-destination" replace />;
  } else if (role && user.role !== role) {
    return <Navigate to="/" replace />;
  }
  return <Layout>{children}</Layout>;
};

// Auth-required but no Layout sidebar (used for destination selector)
const AuthRoute = ({ children }) => {
  const { user, loading, orgsLoading } = useAuth();
  if (loading || orgsLoading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading, orgsLoading } = useAuth();
  if (loading || orgsLoading) return <LoadingScreen />;
  if (user) {
    if (user.role === 'ADMIN') return <Navigate to="/admin" replace />;
    return <Navigate to="/select-destination" replace />;
  }
  return children;
};

const RootRedirect = () => {
  const { user, loading, orgs, orgsLoading, switchOrg } = useAuth();
  if (loading || orgsLoading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'ADMIN') return <Navigate to="/admin" replace />;

  const loginDefault = getLoginDefault();
  if (loginDefault === 'seeker') return <Navigate to="/seeker" replace />;
  if (loginDefault?.startsWith('org:')) {
    const orgId = loginDefault.slice(4);
    const org = orgs.find((o) => o.id === orgId);
    if (org) { switchOrg(org); return <Navigate to="/employer" replace />; }
    // Org no longer accessible — clear stale default and fall through to selector
    clearLoginDefault();
  }

  return <Navigate to="/select-destination" replace />;
};

// ── App ───────────────────────────────────────────────

const AppRoutes = () => (
  <Routes>
    {/* Root */}
    <Route path="/" element={<RootRedirect />} />

    {/* Public */}
    <Route path="/login"    element={<PublicRoute><LoginPage /></PublicRoute>} />
    <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />

    {/* Destination selector — auth-required, no sidebar */}
    <Route path="/select-destination" element={
      <AuthRoute><DestinationSelector /></AuthRoute>
    } />

    {/* ── Employer ── */}
    <Route path="/employer" element={
      <ProtectedRoute role="EMPLOYER"><EmployerDashboard /></ProtectedRoute>
    } />
    <Route path="/employer/profile/workplaces" element={
      <ProtectedRoute role="EMPLOYER"><EmployerWorkplaces /></ProtectedRoute>
    } />
    <Route path="/employer/workplaces" element={<Navigate to="/employer/profile/workplaces" replace />} />
    <Route path="/employer/profile/jobs" element={
      <ProtectedRoute role="EMPLOYER"><EmployerJobLibrary /></ProtectedRoute>
    } />
    <Route path="/employer/profile/positions" element={<Navigate to="/employer/profile/jobs" replace />} />
    <Route path="/employer/positions" element={<Navigate to="/employer/profile/jobs" replace />} />
    <Route path="/employer/profile/culture" element={<Navigate to="/employer/profile/overview" replace />} />
    <Route path="/employer/profile/benefits" element={<Navigate to="/employer/profile/overview" replace />} />
    <Route path="/employer/job-ads" element={
      <ProtectedRoute role="EMPLOYER"><EmployerJobAds /></ProtectedRoute>
    } />
    <Route path="/employer/job-ads/:adId" element={
      <ProtectedRoute role="EMPLOYER"><JobAdMockup /></ProtectedRoute>
    } />
    <Route path="/employer/candidates" element={
      <ProtectedRoute role="EMPLOYER"><EmployerCandidates /></ProtectedRoute>
    } />
    <Route path="/employer/candidates/:matchId" element={
      <ProtectedRoute role="EMPLOYER"><EmployerMatchDetail /></ProtectedRoute>
    } />
    <Route path="/employer/jobs" element={<Navigate to="/employer/job-ads" replace />} />
    <Route path="/employer/org" element={
      <ProtectedRoute role="EMPLOYER"><OrgSettings /></ProtectedRoute>
    } />
    <Route path="/employer/settings" element={<Navigate to="/employer/settings/members" replace />} />
    <Route path="/employer/settings/members" element={
      <ProtectedRoute role="EMPLOYER"><EmployerMembers /></ProtectedRoute>
    } />
    <Route path="/employer/profile/overview" element={
      <ProtectedRoute role="EMPLOYER"><EmployerOverview /></ProtectedRoute>
    } />
    <Route path="/employer/profile/team" element={
      <ProtectedRoute role="EMPLOYER"><EmployerTeam /></ProtectedRoute>
    } />
    <Route path="/employer/profile/contacts" element={
      <ProtectedRoute role="EMPLOYER"><EmployerContacts /></ProtectedRoute>
    } />
    <Route path="/employer/profile/media" element={
      <ProtectedRoute role="EMPLOYER"><EmployerMedia /></ProtectedRoute>
    } />

    {/* ── Job Seeker ── */}
    <Route path="/seeker" element={
      <ProtectedRoute role="JOB_SEEKER"><SeekerDashboard /></ProtectedRoute>
    } />
    <Route path="/seeker/jobs" element={
      <ProtectedRoute role="JOB_SEEKER"><SeekerJobs /></ProtectedRoute>
    } />
    <Route path="/seeker/jobs/:id" element={
      <ProtectedRoute role="JOB_SEEKER"><SeekerJobDetail /></ProtectedRoute>
    } />
    <Route path="/seeker/profile" element={
      <ProtectedRoute role="JOB_SEEKER"><SeekerProfile /></ProtectedRoute>
    } />
    <Route path="/seeker/profile/cv/:id" element={
      <ProtectedRoute role="JOB_SEEKER"><SeekerCVEditor /></ProtectedRoute>
    } />
    <Route path="/seeker/cv-review/:id" element={
      <ProtectedRoute role="JOB_SEEKER"><SeekerCVImport /></ProtectedRoute>
    } />
    <Route path="/seeker/profile/personal" element={
      <ProtectedRoute role="JOB_SEEKER"><SeekerPersonal /></ProtectedRoute>
    } />
    <Route path="/seeker/profile/summary" element={
      <ProtectedRoute role="JOB_SEEKER"><SeekerSummary /></ProtectedRoute>
    } />
    <Route path="/seeker/profile/skills" element={
      <ProtectedRoute role="JOB_SEEKER"><SeekerSkills /></ProtectedRoute>
    } />
    <Route path="/seeker/profile/languages" element={
      <ProtectedRoute role="JOB_SEEKER"><SeekerLanguages /></ProtectedRoute>
    } />
    <Route path="/seeker/profile/qualifications" element={
      <ProtectedRoute role="JOB_SEEKER"><SeekerQualifications /></ProtectedRoute>
    } />
    <Route path="/seeker/profile/experience" element={
      <ProtectedRoute role="JOB_SEEKER"><SeekerExperience /></ProtectedRoute>
    } />
    <Route path="/seeker/availability" element={
      <ProtectedRoute role="JOB_SEEKER"><SeekerAvailability /></ProtectedRoute>
    } />
    <Route path="/seeker/profile/work-preferences" element={
      <ProtectedRoute role="JOB_SEEKER"><SeekerWorkPreferences /></ProtectedRoute>
    } />
    <Route path="/seeker/profile/references" element={
      <ProtectedRoute role="JOB_SEEKER"><SeekerReferences /></ProtectedRoute>
    } />

    {/* ── Admin ── */}
    <Route path="/admin" element={
      <ProtectedRoute role="ADMIN"><AdminDashboard /></ProtectedRoute>
    } />
    <Route path="/admin/config" element={
      <ProtectedRoute role="ADMIN"><AdminConfig /></ProtectedRoute>
    } />

    {/* Fallback */}
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: '#161D2F',
              color: '#F3F4F6',
              border: '1px solid #1E2A45',
              fontFamily: '"DM Sans", sans-serif',
              fontSize: '14px',
            },
            success: { iconTheme: { primary: '#14B8A6', secondary: '#0A0F1E' } },
            error:   { iconTheme: { primary: '#F43F5E', secondary: '#0A0F1E' } },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
